import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ForwardedRef, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type WheelEvent as ReactWheelEvent } from 'react';

export interface Viewport {
  x: number;
  y: number;
  scale: number;
}

export interface CanvasHandle {
  /** Centre on a point in layout coordinates. */
  centerOn: (x: number, y: number, scale?: number, animate?: boolean) => void;
  /**
   * Frame these bounds. `home` is where to stand when they cannot be framed —
   * a family too large to fit on the screen even from as far back as the camera
   * goes. See the note in `fit`.
   */
  fit: (bounds: { minX: number; minY: number; width: number; height: number }, padding?: number, animate?: boolean, home?: { x: number; y: number }) => void;
  zoomBy: (factor: number) => void;
  getViewport: () => Viewport;
  /** Size of the visible canvas in CSS pixels. */
  getSize: () => { width: number; height: number };
  panBy: (dx: number, dy: number) => void;
}

export const MIN_SCALE = 0.18;
export const MAX_SCALE = 2.2;

/**
 * Pan/zoom surface supporting mouse drag, touch drag, pinch, wheel, trackpad
 * and keyboard — plus visible zoom controls supplied by the parent, so no
 * interaction depends on precision dragging (section 9.4 / 15.1).
 *
 * **The camera is written straight onto the element, not through React.**
 *
 * It used to live in a `useState`, and the tree was drawn by a render prop that
 * was handed the camera — so every pointermove of a drag re-rendered the whole
 * family: every circle, every line, sixty times a second. With thirty people
 * nobody could tell. With several hundred it is the difference between a tree
 * that follows the finger and one that chugs after it, on exactly the phones
 * most of the family will be holding.
 *
 * So the truth is a ref, the move is one `transform` on one element (the
 * compositor carries every child along for free — the same thing the
 * constellation does, see MemoryNodes), and whoever is drawing the tree is
 * *told* where the camera went at most once a frame (`onViewportChange`). What
 * they do with that is up to them; TreeScreen only re-renders when it crosses
 * something that changes what is drawn — a zoom band, the edge of what it has
 * mounted — and never merely because the camera moved.
 */
export const TreeCanvas = forwardRef(function TreeCanvas(
  {
    children,
    reducedMotion,
    onViewportChange,
    ariaLabel,
    onBackgroundClick,
  }: {
    children: ReactNode;
    reducedMotion: boolean;
    /** Where the camera is now. Called at most once per frame, however many pointer events arrived in it. */
    onViewportChange?: (vp: Viewport) => void;
    ariaLabel?: string;
    onBackgroundClick?: () => void;
  },
  ref: ForwardedRef<CanvasHandle>,
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const worldRef = useRef<HTMLDivElement | null>(null);
  const vpRef = useRef<Viewport>({ x: 0, y: 0, scale: 0.75 });
  const animRef = useRef<number | null>(null);
  const notifyRef = useRef<number | null>(null);
  // Read through a ref so `apply` keeps its identity whatever the parent passes on each render.
  const onChangeRef = useRef(onViewportChange);
  onChangeRef.current = onViewportChange;
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const dragState = useRef<{ startVp: Viewport; startX: number; startY: number; pinchDist: number; pinchCenter: { x: number; y: number } } | null>(null);
  const [dragging, setDragging] = useState(false);

  const write = useCallback(() => {
    const world = worldRef.current;
    const v = vpRef.current;
    if (world) world.style.transform = `translate3d(${v.x}px, ${v.y}px, 0) scale(${v.scale})`;
  }, []);

  const apply = useCallback(
    (next: Viewport) => {
      vpRef.current = next;
      write();
      // One telling per frame: a fast finger sends several moves between two paints.
      if (notifyRef.current === null) {
        notifyRef.current = requestAnimationFrame(() => {
          notifyRef.current = null;
          onChangeRef.current?.(vpRef.current);
        });
      }
    },
    [write],
  );

  // React never owns the transform (it is not in the style prop below), so it is put there once by hand.
  useLayoutEffect(write, [write]);

  const animateTo = useCallback(
    (target: Viewport, animate: boolean) => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (!animate || reducedMotion) {
        apply(target);
        return;
      }
      const from = { ...vpRef.current };
      const start = performance.now();
      const dur = 480;
      const step = (now: number) => {
        const p = Math.min(1, (now - start) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        apply({
          x: from.x + (target.x - from.x) * e,
          y: from.y + (target.y - from.y) * e,
          scale: from.scale + (target.scale - from.scale) * e,
        });
        if (p < 1) animRef.current = requestAnimationFrame(step);
      };
      animRef.current = requestAnimationFrame(step);
    },
    [apply, reducedMotion],
  );

  /**
   * Camera moves that arrive before the canvas has been laid out (first paint,
   * a screen that is still fading in) would compute against a 0×0 box and
   * leave the tree off-screen — the "I see nothing" bug. Wait for a real size.
   */
  const whenMeasured = useCallback((fn: (rect: DOMRect) => void) => {
    let tries = 0;
    const attempt = () => {
      const host = hostRef.current;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) fn(rect);
      else if (tries++ < 30) requestAnimationFrame(attempt);
    };
    attempt();
  }, []);

  const zoomAround = useCallback(
    (clientLocalX: number, clientLocalY: number, factor: number) => {
      const cur = vpRef.current;
      const next = clampScale(cur.scale * factor);
      if (next === cur.scale) return;
      const k = next / cur.scale;
      apply({ scale: next, x: clientLocalX - (clientLocalX - cur.x) * k, y: clientLocalY - (clientLocalY - cur.y) * k });
    },
    [apply],
  );

  useImperativeHandle(
    ref,
    (): CanvasHandle => ({
      getViewport: () => vpRef.current,
      getSize: () => {
        const rect = hostRef.current?.getBoundingClientRect();
        return { width: rect?.width ?? 0, height: rect?.height ?? 0 };
      },
      centerOn: (x, y, scale, animate = true) => {
        whenMeasured((rect) => {
          const s = clampScale(scale ?? vpRef.current.scale);
          animateTo({ x: rect.width / 2 - x * s, y: rect.height / 2 - y * s, scale: s }, animate);
        });
      },
      fit: (bounds, padding = 60, animate = true, home) => {
        whenMeasured((rect) => {
        /*
         * The inset is a share of the screen, not a fixed number of pixels. 90px
         * on each side of a 390px phone leaves the family a 210px strip in the
         * middle of an empty screen — the whole tree is "visible" and far too
         * small to read. A tenth of the shorter side keeps the same generous
         * feel on a desktop and gives a phone its screen back.
         */
        const pad = Math.min(padding, Math.min(rect.width, rect.height) * 0.1);
        const availW = Math.max(120, rect.width - pad * 2);
        const availH = Math.max(120, rect.height - pad * 2);
        const wanted = Math.min(availW / Math.max(1, bounds.width), availH / Math.max(1, bounds.height), 1.05);
        const s = clampScale(wanted);
        /*
         * A family of a thousand is a dozen phone screens wide even from as far
         * back as the camera goes. Framing "all of it" then means standing in the
         * middle of its bounding box, which is as likely as not an empty stretch
         * between two branches — the app would open on nothing. When it cannot
         * all be seen, stand back as far as possible *over somebody*: the caller
         * says who (the viewer's own circle), and the rest is a drag away.
         */
        const tooBig = wanted < MIN_SCALE;
        const cx = tooBig && home ? home.x : bounds.minX + bounds.width / 2;
        const cy = tooBig && home ? home.y : bounds.minY + bounds.height / 2;
        animateTo({ x: rect.width / 2 - cx * s, y: rect.height / 2 - cy * s, scale: s }, animate);
        });
      },
      zoomBy: (factor) => {
        const host = hostRef.current;
        if (!host) return;
        const rect = host.getBoundingClientRect();
        zoomAround(rect.width / 2, rect.height / 2, factor);
      },
      panBy: (dx, dy) => {
        apply({ ...vpRef.current, x: vpRef.current.x - dx, y: vpRef.current.y - dy });
      },
    }),
    [animateTo, apply, whenMeasured, zoomAround],
  );

  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    const host = hostRef.current;
    if (!host) return;
    // (The page is kept from scrolling by the non-passive listener below; React's own wheel listener is passive,
    // and calling preventDefault in it only logs an error on every notch of the wheel.)
    const rect = host.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    if (e.ctrlKey || e.metaKey) {
      zoomAround(localX, localY, Math.exp(-e.deltaY * 0.01));
    } else if (Math.abs(e.deltaY) > 0 && !e.shiftKey && Math.abs(e.deltaX) < 1) {
      zoomAround(localX, localY, Math.exp(-e.deltaY * 0.0022));
    } else {
      apply({ ...vpRef.current, x: vpRef.current.x - e.deltaX, y: vpRef.current.y - e.deltaY });
    }
  };

  // Non-passive wheel listener so preventDefault works on all browsers
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const handler = (e: WheelEvent) => e.preventDefault();
    host.addEventListener('wheel', handler, { passive: false });
    return () => host.removeEventListener('wheel', handler);
  }, []);

  /**
   * A finger (or mouse) that lands on a circle still pans and pinches the
   * tree — the circle only counts as tapped when the pointer is released
   * quickly and without moving. `gesture` remembers whether this press moved
   * or lingered so the browser's click can be swallowed afterwards.
   */
  const gesture = useRef<{ startedAt: number; moved: boolean; multi: boolean }>({ startedAt: 0, moved: false, multi: false });
  /** The circle the press started on. Its tap is fired from pointer-up (see endPointer). */
  const downNode = useRef<HTMLElement | null>(null);
  const TAP_MAX_MS = 400;
  const TAP_MAX_PX = 6;

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const host = hostRef.current;
    if (!host) return;
    // A hand on the tree takes it over from any camera move still under way.
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
    if (pointers.current.size === 0) downNode.current = (e.target as HTMLElement).closest<HTMLElement>('[data-node]');
    try {
      host.setPointerCapture(e.pointerId);
    } catch {
      /* a pointer the browser no longer knows about: the drag still works, it just is not captured */
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      gesture.current = { startedAt: performance.now(), moved: false, multi: false };
      dragState.current = { startVp: { ...vpRef.current }, startX: e.clientX, startY: e.clientY, pinchDist: 0, pinchCenter: { x: 0, y: 0 } };
      setDragging(true);
    } else if (pointers.current.size === 2) {
      gesture.current.multi = true;
      const [a, b] = [...pointers.current.values()];
      const rect = host.getBoundingClientRect();
      dragState.current = {
        startVp: { ...vpRef.current },
        startX: (a.x + b.x) / 2,
        startY: (a.y + b.y) / 2,
        pinchDist: Math.hypot(a.x - b.x, a.y - b.y),
        pinchCenter: { x: (a.x + b.x) / 2 - rect.left, y: (a.y + b.y) / 2 - rect.top },
      };
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId) || !dragState.current) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const st = dragState.current;
    if (!gesture.current.moved && Math.hypot(e.clientX - st.startX, e.clientY - st.startY) > TAP_MAX_PX) gesture.current.moved = true;
    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (st.pinchDist > 0) {
        const factor = dist / st.pinchDist;
        const nextScale = clampScale(st.startVp.scale * factor);
        const k = nextScale / st.startVp.scale;
        const cx = st.pinchCenter.x;
        const cy = st.pinchCenter.y;
        apply({ scale: nextScale, x: cx - (cx - st.startVp.x) * k, y: cy - (cy - st.startVp.y) * k });
      }
    } else {
      apply({ ...vpRef.current, x: st.startVp.x + (e.clientX - st.startX), y: st.startVp.y + (e.clientY - st.startY) });
    }
  };

  const endPointer = (e: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    try {
      hostRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (pointers.current.size === 0) {
      const g = gesture.current;
      const isTap = !g.moved && !g.multi && performance.now() - g.startedAt <= TAP_MAX_MS;
      dragState.current = null;
      setDragging(false);
      const node = downNode.current;
      downNode.current = null;
      if (isTap) {
        // A quick, still press that began on a circle is that circle's tap. It is fired here
        // because the host captures the pointer: with a mouse the browser's own click then
        // lands on the host (the common ancestor), never on the circle's button, so waiting
        // for it would mean mouse clicks select nothing. `.click()` is a synthetic click
        // (detail 0), which onClickCapture below lets through.
        if (node) node.click();
        else onBackgroundClick?.();
      }
    } else if (pointers.current.size === 1) {
      const [only] = [...pointers.current.values()];
      dragState.current = { startVp: { ...vpRef.current }, startX: only.x, startY: only.y, pinchDist: 0, pinchCenter: { x: 0, y: 0 } };
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = 80;
    const vp = vpRef.current;
    switch (e.key) {
      case 'ArrowLeft': apply({ ...vp, x: vp.x + step }); break;
      case 'ArrowRight': apply({ ...vp, x: vp.x - step }); break;
      case 'ArrowUp': apply({ ...vp, y: vp.y + step }); break;
      case 'ArrowDown': apply({ ...vp, y: vp.y - step }); break;
      case '+': case '=': zoomAround(0, 0, 1.2); break;
      case '-': case '_': zoomAround(0, 0, 1 / 1.2); break;
      default: return;
    }
    e.preventDefault();
  };

  useEffect(
    () => () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (notifyRef.current !== null) cancelAnimationFrame(notifyRef.current);
      // Forgotten as well as cancelled, or the "one telling per frame" guard would never let another through.
      animRef.current = null;
      notifyRef.current = null;
    },
    [],
  );

  return (
    <div
      ref={hostRef}
      className={`tree-canvas ${dragging ? 'dragging' : ''}`}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onClickCapture={(e) => {
        // Taps on circles are handled on pointer-up; the browser's own pointer click (after a drag, a
        // long hold, or a clean tap alike) must not reach the circle a second time. Keyboard
        // activation and our synthetic tap arrive with detail 0 and pass.
        if (e.detail !== 0 && (e.target as HTMLElement).closest('[data-node]')) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="application"
      aria-label={ariaLabel}
    >
      {/* No `style` here on purpose: the transform belongs to `write()` above, and a style prop would
          let a re-render put a stale camera back. */}
      <div ref={worldRef} className="tree-world">
        {children}
      </div>
    </div>
  );
});

function clampScale(s: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
}
